/**
 * Locked, never absent (marketplace.md 4.6). Feature modules wrap a gated
 * screen or panel in `<ModuleGate feature="reviews">`: with access it renders
 * the children; without it renders the Marketplace's `LockedModulePanel`
 * where the feature would be, with the price, the trial for this office (or
 * "Request" for staff) and a link to the module page. Read-only modules keep
 * their content and show a "Read-only, turn back on" bar instead.
 *
 * Access is asked per office in scope (`GET /billing-saas/access`), under a
 * `marketplace` query key, so the `marketplace.entitlement` live event a
 * trial or turn-on publishes unlocks every open tab within seconds, without
 * a reload, for any module key (even before its module ships a manifest).
 */
import { useQueries } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { MarketplaceModuleKey } from '@velora/contracts/marketplace';
import { LockedModulePanel } from '@velora/ui';
import { Lock } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { notify, useLive, useOfficeScope, usePermission } from '../../../app/shell.js';
import {
  isBillingUnavailable,
  marketplaceApi,
  marketplaceKeys,
  marketplaceRetry,
  useStore,
} from '../api.js';
import { ModuleIcon } from '../components/ModuleIcon.js';
import { RequestDrawer } from '../components/RequestDrawer.js';
import { catalog, money } from '../model.js';
import '../marketplace.css';

export interface MarketplaceLockProps {
  feature: MarketplaceModuleKey;
  className?: string;
}

export interface ModuleAccess {
  loading: boolean;
  /** Full access at one or more offices in scope. */
  active: boolean;
  /** Reads allowed somewhere in scope (full or read-only). */
  readable: boolean;
  trialAvailable: boolean;
  /** Why it is locked (the 402 reason), when locked everywhere. */
  reason: string | null;
  /**
   * The server has no add-on billing set up (503 `billing_unavailable`), so
   * access cannot be checked or bought here. Still locked (fails closed);
   * the lock says so instead of offering a trial that cannot start.
   */
  unavailable: boolean;
}

/** Marketplace access to `feature` across the offices in the selector. */
export function useModuleAccess(feature: MarketplaceModuleKey): ModuleAccess {
  const { locationIds } = useOfficeScope();
  const results = useQueries({
    queries: locationIds.map((id) => ({
      queryKey: [...marketplaceKeys.all, 'access', feature, id] as const,
      queryFn: () => marketplaceApi.access(feature, id, 'read'),
      staleTime: 30_000,
      // A refused or failed check locks (fails closed) without a long retry
      // wait; a missing billing setup is not retried at all.
      retry: (n: number, e: unknown) => marketplaceRetry(n, e) && n < 1,
      retryDelay: 300,
    })),
  });
  const data = results.map((r) => r.data).filter((d) => d !== undefined);
  const full = data.some((d) => d.allowed && d.access === 'full');
  const readable = data.some((d) => d.allowed);
  const denied = data.find((d) => !d.allowed);
  return {
    loading: results.some((r) => r.isPending),
    active: full,
    readable,
    trialAvailable: data.some((d) => !d.allowed && d.body.trial_available),
    reason: !readable && denied && !denied.allowed ? denied.body.reason : null,
    unavailable:
      results.length > 0 && results.every((r) => r.isError && isBillingUnavailable(r.error)),
  };
}

/**
 * What a locked module offers (ADM-11). Buyers get the trial or "Enable"
 * only when this server can bill; without Square (`billingOff`: the store is
 * served read-only) the panel says "Billing not enabled" instead of a button
 * that leads nowhere, and staff can still ask an admin (module requests are
 * Velora rows and need no Square). When every billing route answers 503
 * (`unavailable`) nothing is offered.
 */
export function lockPresentation(input: {
  pitch: string;
  unavailable: boolean;
  billingOff: boolean;
  canBuy: boolean;
}): { pitch: string; action: 'trial' | 'request' | 'none' } {
  if (input.unavailable) {
    // Every billing route answers 503 (an older server): requests fail too.
    return {
      pitch: `${input.pitch} Add-ons cannot be started on this server yet; Velora support finishes the billing setup.`,
      action: 'none',
    };
  }
  if (!input.canBuy) return { pitch: input.pitch, action: 'request' };
  if (input.billingOff) {
    return {
      pitch: `${input.pitch} Billing not enabled: add-on billing is not set up on this server, so it cannot be turned on here yet. Learn more shows what it does.`,
      action: 'none',
    };
  }
  return { pitch: input.pitch, action: 'trial' };
}

export function MarketplaceLock({ feature, className }: MarketplaceLockProps) {
  const m = catalog(feature);
  const navigate = useNavigate();
  const canBuy = usePermission('marketplace.purchase');
  const ent = useModuleAccess(feature);
  const { scope, locationIds } = useOfficeScope();
  const [requesting, setRequesting] = useState(false);
  // Staff ask from right here (MK-10): the drawer posts the request with its
  // reason; admins see it in Marketplace, Requests and get a notification.
  const store = useStore();
  if (!m) return null;
  // Buyers land on the module page with the trial (or turn-on) sheet open.
  const go = (trial: boolean) =>
    void navigate({
      to: `/marketplace/${m.key}`,
      search: trial ? { trial: 1 } : {},
    } as never);
  const offices = (store.data?.offices ?? []).filter((o) => locationIds.includes(o.id));
  const billingOff = store.data?.squareMode === 'not_configured';
  const shown = lockPresentation({
    pitch:
      ent.unavailable || billingOff
        ? m.lockedPitch
        : (REASON_COPY[ent.reason ?? ''] ?? m.lockedPitch),
    unavailable: ent.unavailable,
    billingOff,
    canBuy,
  });
  return (
    <div
      className="mk-lock"
      data-feature={feature}
      data-reason={
        ent.unavailable ? 'billing_unavailable' : billingOff ? 'billing_off' : (ent.reason ?? '')
      }
    >
      <LockedModulePanel
        {...(className ? { className } : {})}
        moduleName={m.name}
        pitch={shown.pitch}
        features={m.features.slice(0, 3).map((f) => f.title)}
        price={`${money(m.priceCents)} per office per month${m.pricing.placeholder ? ' (placeholder price)' : ''}`}
        icon={<ModuleIcon module={m} />}
        {...(m.trialDays > 0 && ent.trialAvailable && shown.action === 'trial'
          ? { trialDays: m.trialDays }
          : {})}
        {...(shown.action === 'trial'
          ? { onStartTrial: () => go(true) }
          : shown.action === 'request'
            ? {
                requestAccessFrom: scope.kind === 'all' ? 'an admin' : 'your admin',
                onRequestAccess: () => setRequesting(true),
              }
            : {})}
        onLearnMore={() => go(false)}
      />
      {canBuy ? null : (
        <RequestDrawer
          open={requesting && offices.length > 0}
          onOpenChange={setRequesting}
          module={m}
          offices={offices}
          defaultOfficeId={scope.kind === 'location' ? scope.locationId : (offices[0]?.id ?? null)}
          // Recommendations stay on the Store page (owner-research-marketplace.md "No nagging").
          suggestedReason={null}
        />
      )}
    </div>
  );
}

/**
 * Tells admins (anyone with `marketplace.purchase`) when staff ask for a
 * module (MK-10): a bell notification that opens Marketplace, Requests. The
 * shell mounts it once; the request list itself refetches on the same event.
 */
export function MarketplaceRequestNotifier() {
  const canBuy = usePermission('marketplace.purchase');
  useLive('marketplace.request', (e) => {
    if (!canBuy || e.op !== 'created') return;
    notify({
      key: `marketplace.request:${e.id}`,
      title: 'A module was requested',
      body: 'Someone on your team asked to turn on a Marketplace module.',
      tone: 'info',
      href: '/marketplace/requests',
    });
  });
  return null;
}

const REASON_COPY: Record<string, string> = {
  trial_ended:
    'The free trial at this office has ended. Turn it on to keep using it; nothing was deleted.',
  trial_used: 'This office already used its free trial. Turn it on any time, month to month.',
  past_due: 'A payment for this module failed. Update the card to keep it running.',
  paused: 'This module is paused at this office. Turn it back on to pick up where you left off.',
  canceled:
    'This module was turned off. Your data is kept for 90 days; turn it back on to see it again.',
};

/** Renders `children` with access, the lock panel without it, and a read-only bar when lapsed. */
export function ModuleGate({
  feature,
  children,
}: {
  feature: MarketplaceModuleKey;
  children: ReactNode;
}) {
  const ent = useModuleAccess(feature);
  const navigate = useNavigate();
  const m = catalog(feature);
  if (ent.loading) return null;
  if (ent.active) return <>{children}</>;
  if (ent.readable && m) {
    return (
      <>
        <div className="mk-readonly" role="status">
          <Lock size={14} aria-hidden />
          <span>
            {m.name} is read-only here. You can view and export, but changes and PMS write-back are
            off.
          </span>
          <button
            type="button"
            onClick={() => void navigate({ to: `/marketplace/${m.key}` } as never)}
          >
            Turn back on
          </button>
        </div>
        {children}
      </>
    );
  }
  return (
    <div className="mk-gate">
      <MarketplaceLock feature={feature} />
    </div>
  );
}
